import { Program } from '../Program/index.js';

export type StackFrame = {
    ip: number;
    program: Program;
    returnIp: number;
    name: string;
    isInterrupt: boolean;
    isModule: boolean;
    moduleName?: string;
    locals: Record<string, any>;
    localSlots: any[];
    exports: Record<string, any>;
    sleepTimer: number;
    isFastCall?: boolean;
}

type VMEvent = {
    name: string;
    args: any[];
}

type ModuleScopeMap = Record<string, Record<string, any>>;

const FRAME_KIND_SLOW = 0;
const FRAME_KIND_FAST = 1;

/**
 * Represents the current state of the virtual machine.
 *
 * All data inside the State must be considered volatile and can change at any
 * time.
 */
export class State
{
    public ip: number = 0;
    public isHalted: boolean = false;
    public sleepTime: number = 0;
    public deltaTime: number = 0;
    public wallTime: number = 0;
    public isAwaitingPromise: boolean = false;

    private _program: Program;
    private _mainScope: Record<string, any>;
    private _programs: Record<string, Program> = {};
    private _stack: any[]                      = [];
    private _frames: StackFrame[]              = [];
    private _eventQueue: VMEvent[]             = [];
    private _scopes: ModuleScopeMap            = {};

    // Mixed frame stack:
    // - Slow frames are stored as full objects in _frames.
    // - Fast call frames are stored in compact parallel arrays.
    private _frameKinds: number[]              = [];
    private _fastReturnIps: number[]           = [];
    private _fastIps: number[]                 = [];
    private _fastPrograms: Program[]           = [];
    private _fastNames: string[]               = [];
    private _fastModuleNames: (string | undefined)[] = [];
    private _fastSleepTimers: number[]         = [];
    private _fastLocalSlots: any[][]           = [];
    private _fastLocals: Array<Record<string, any> | null> = [];

    private _topSlowFrame: StackFrame | null   = null;
    private _currentProgram: Program;
    private _currentScope: Record<string, any>;
    public currentLocalSlots: any[];

    constructor(program: Program, globals: Record<string, any> = {})
    {
        this._program                = program;
        this._programs[program.hash] = program;
        this._scopes[program.hash]   = globals;
        this._mainScope              = globals;
        this._currentProgram         = program;
        this._currentScope           = globals;
        this.currentLocalSlots       = [];
    }

    public get currentProgram(): Program
    {
        return this._currentProgram;
    }

    public import(data: {
        stack?: any[];
        programs?: Record<string, Program>,
        frames?: StackFrame[];
        scopes?: Record<string, any>,
        events?: VMEvent[],
        sleepTime?: number
        deltaTime?: number
    })
    {
        if (data.stack) {
            this._stack = data.stack;
        }

        if (data.programs) {
            this._programs = data.programs;
        }

        if (data.scopes) {
            this._scopes = data.scopes;
            this._mainScope = this._scopes[this._program.hash] ?? {};
        }

        if (data.events) {
            this._eventQueue = data.events;
        }

        if (typeof data.sleepTime === 'number') {
            this.sleepTime = data.sleepTime;
        }

        if (typeof data.deltaTime === 'number') {
            this.deltaTime = data.deltaTime;
        }

        if (data.frames) {
            this._frames = [];
            this._frameKinds = [];
            this._fastReturnIps = [];
            this._fastIps = [];
            this._fastPrograms = [];
            this._fastNames = [];
            this._fastModuleNames = [];
            this._fastSleepTimers = [];
            this._fastLocalSlots = [];
            this._fastLocals = [];

            for (const frame of data.frames) {
                if (frame.isFastCall) {
                    this._frameKinds.push(FRAME_KIND_FAST);
                    this._fastReturnIps.push(frame.returnIp);
                    this._fastIps.push(frame.ip);
                    this._fastPrograms.push(frame.program);
                    this._fastNames.push(frame.name);
                    this._fastModuleNames.push(frame.moduleName);
                    this._fastSleepTimers.push(frame.sleepTimer ?? 0);
                    this._fastLocalSlots.push(Array.isArray(frame.localSlots) ? frame.localSlots : []);
                    this._fastLocals.push(frame.locals ?? null);
                } else {
                    if (! Array.isArray(frame.localSlots)) frame.localSlots = [];
                    if (! frame.locals) frame.locals = {};
                    if (! frame.exports) frame.exports = {};
                    this._frames.push(frame);
                    this._frameKinds.push(FRAME_KIND_SLOW);
                }
            }
        }

        this._topSlowFrame = this._frames.length > 0 ? this._frames[this._frames.length - 1] : null;
        this.refreshExecutionContext();
    }

    public get stack(): any[]
    {
        return this._stack;
    }

    public get frames(): StackFrame[]
    {
        const result: StackFrame[] = [];
        let slowIndex = 0;
        let fastIndex = 0;

        for (const kind of this._frameKinds) {
            if (kind === FRAME_KIND_SLOW) {
                result.push(this._frames[slowIndex++]);
            } else {
                result.push({
                    returnIp:    this._fastReturnIps[fastIndex],
                    ip:          this._fastIps[fastIndex],
                    program:     this._fastPrograms[fastIndex],
                    name:        this._fastNames[fastIndex],
                    isInterrupt: false,
                    isModule:    false,
                    moduleName:  this._fastModuleNames[fastIndex],
                    sleepTimer:  this._fastSleepTimers[fastIndex],
                    locals:      this._fastLocals[fastIndex] ?? {},
                    localSlots:  this._fastLocalSlots[fastIndex],
                    exports:     {},
                    isFastCall:  true,
                });
                fastIndex++;
            }
        }

        return result;
    }

    public get scopes(): Record<string, any>
    {
        return this._scopes;
    }

    public get eventQueue(): VMEvent[]
    {
        return this._eventQueue;
    }

    public get programs(): Record<string, Program>
    {
        return this._programs;
    }

    public push(val: any)
    {
        this._stack.push(val);
    }

    public pop(): any
    {
        return this._stack.pop();
    }

    public peek(): any
    {
        return this._stack[this._stack.length - 1];
    }

    public pushFrame(returnIp: number, options: PushFrameOptions | string = {}): StackFrame
    {
        if (typeof options === 'string') {
            this.pushCallFrame(returnIp, options);

            return this.topFrame!;
        }

        const currentProgram = this._currentProgram;
        const program        = options.program ?? currentProgram;
        const scopeName      = program.hash;
        if (typeof this._scopes[scopeName] === 'undefined') {
            this._scopes[scopeName] = {};
        }

        if (typeof this._programs[scopeName] === 'undefined') {
            this._programs[scopeName] = program;
        } else if (options.program && this._programs[scopeName] !== options.program) {
            throw new Error(`Program hash collision detected: ${scopeName}`);
        }

        const frame = {
            returnIp,
            ip:          this.ip,
            program,
            name:        options.name ?? '<anonymous>',
            isInterrupt: options.isInterrupt ?? false,
            isModule:    options.isModule ?? false,
            moduleName:  options.moduleName ?? options.program?.moduleName ?? this.topModuleName,
            sleepTimer:  0,
            locals:      {},
            localSlots:  [],
            exports:     {},
            isFastCall:  false,
        } satisfies StackFrame;

        this._frames.push(frame);
        this._frameKinds.push(FRAME_KIND_SLOW);
        this._topSlowFrame = frame;

        this._currentProgram = frame.program;
        this._currentScope = this._scopes[scopeName];
        this.currentLocalSlots = frame.localSlots;

        return frame;
    }

    public pushCallFrame(returnIp: number, name: string): void
    {
        const program   = this._currentProgram;
        const scopeName = program.hash;

        this._frameKinds.push(FRAME_KIND_FAST);
        this._fastReturnIps.push(returnIp);
        this._fastIps.push(this.ip);
        this._fastPrograms.push(program);
        this._fastNames.push(name);
        this._fastModuleNames.push(this.topModuleName);
        this._fastSleepTimers.push(0);
        this._fastLocalSlots.push([]);
        this._fastLocals.push(null);

        this._currentProgram = program;
        this._currentScope = this._scopes[scopeName] ?? this._mainScope;
        this.currentLocalSlots = this._fastLocalSlots[this._fastLocalSlots.length - 1];
    }

    public popFastCallFrame(): number | undefined
    {
        if (this._frameKinds.length === 0 || this._frameKinds[this._frameKinds.length - 1] !== FRAME_KIND_FAST) {
            return undefined;
        }

        this._frameKinds.pop();
        this._fastIps.pop();
        this._fastPrograms.pop();
        this._fastNames.pop();
        this._fastModuleNames.pop();
        this._fastSleepTimers.pop();
        this._fastLocalSlots.pop();
        this._fastLocals.pop();
        const returnIp = this._fastReturnIps.pop();

        this.refreshExecutionContext();

        return returnIp;
    }

    public popFrame(): StackFrame | undefined
    {
        if (this._frameKinds.length === 0) {
            return undefined;
        }

        const kind = this._frameKinds[this._frameKinds.length - 1];
        if (kind === FRAME_KIND_FAST) {
            const returnIp = this.popFastCallFrame();
            if (typeof returnIp !== 'number') {
                return undefined;
            }

            return {
                returnIp,
                ip:          this.ip,
                program:     this._currentProgram,
                name:        '<fast>',
                isInterrupt: false,
                isModule:    false,
                moduleName:  this.topModuleName,
                sleepTimer:  0,
                locals:      {},
                localSlots:  [],
                exports:     {},
                isFastCall:  true,
            };
        }

        this._frameKinds.pop();
        const popped = this._frames.pop();
        this._topSlowFrame = this._frames.length > 0 ? this._frames[this._frames.length - 1] : null;
        this.refreshExecutionContext();

        return popped;
    }

    public get topFrame(): StackFrame | null
    {
        if (this._frameKinds.length === 0) {
            return null;
        }

        const topKind = this._frameKinds[this._frameKinds.length - 1];
        if (topKind === FRAME_KIND_SLOW) {
            return this._topSlowFrame;
        }

        const i = this._fastReturnIps.length - 1;

        return {
            returnIp:    this._fastReturnIps[i],
            ip:          this._fastIps[i],
            program:     this._fastPrograms[i],
            name:        this._fastNames[i],
            isInterrupt: false,
            isModule:    false,
            moduleName:  this._fastModuleNames[i],
            sleepTimer:  this._fastSleepTimers[i],
            locals:      this._fastLocals[i] ?? {},
            localSlots:  this._fastLocalSlots[i],
            exports:     {},
            isFastCall:  true,
        };
    }

    public get topMutableFrame(): StackFrame | null
    {
        if (this._frameKinds.length === 0) {
            return null;
        }

        const topKind = this._frameKinds[this._frameKinds.length - 1];
        if (topKind !== FRAME_KIND_SLOW) {
            return null;
        }

        return this._topSlowFrame;
    }

    public get numFrames(): number
    {
        return this._frameKinds.length;
    }

    public get hasFrames(): boolean
    {
        return this._frameKinds.length > 0;
    }

    public getVar(name: string): any
    {
        if (this._frameKinds.length > 0) {
            const topKind = this._frameKinds[this._frameKinds.length - 1];

            if (topKind === FRAME_KIND_SLOW) {
                const locals = this._topSlowFrame?.locals;
                if (locals && name in locals) {
                    return locals[name];
                }
            } else {
                const locals = this._fastLocals[this._fastLocals.length - 1];
                if (locals && name in locals) {
                    return locals[name];
                }
            }
        }

        const scope = this._currentScope;
        if (scope && name in scope) {
            return scope[name];
        }

        if (name in this._mainScope) {
            return this._mainScope[name];
        }

        throw new Error(`The variable "${name}" is not defined.`);
    }

    public setVar(name: string, value: any, local: boolean = false)
    {
        const topKind = this._frameKinds.length > 0 ? this._frameKinds[this._frameKinds.length - 1] : null;
        const scope   = this._currentScope;
        const topSlowFrame = this._topSlowFrame;

        if (local && topKind !== null) {
            if (topKind === FRAME_KIND_SLOW) {
                topSlowFrame!.locals[name] = value;
            } else {
                let locals = this._fastLocals[this._fastLocals.length - 1];
                if (! locals) {
                    locals = {};
                    this._fastLocals[this._fastLocals.length - 1] = locals;
                }
                locals[name] = value;
            }
            return;
        }

        // Module top-level bindings must live in module scope so EXPORT accessors
        // can resolve them reliably across imports.
        if (topKind === FRAME_KIND_SLOW && topSlowFrame?.isModule) {
            if (scope) {
                scope[name] = value;
                return;
            }
        }

        if (topKind === FRAME_KIND_SLOW) {
            const locals = topSlowFrame!.locals;
            if (typeof locals[name] !== 'undefined') {
                locals[name] = value;
                return;
            }
        } else if (topKind === FRAME_KIND_FAST) {
            const locals = this._fastLocals[this._fastLocals.length - 1];
            if (locals && typeof locals[name] !== 'undefined') {
                locals[name] = value;
                return;
            }
        }

        if (scope && typeof scope[name] !== 'undefined') {
            scope[name] = value;
            return;
        }

        if (topKind === FRAME_KIND_SLOW) {
            topSlowFrame!.locals[name] = value;
        } else if (topKind === FRAME_KIND_FAST) {
            let locals = this._fastLocals[this._fastLocals.length - 1];
            if (! locals) {
                locals = {};
                this._fastLocals[this._fastLocals.length - 1] = locals;
            }
            locals[name] = value;
        } else if (scope) {
            scope[name] = value;
        }
    }

    public get isInsideInterrupt(): boolean
    {
        for (let i = this._frames.length - 1; i >= 0; i--) {
            if (this._frames[i].isInterrupt) return true;
        }

        return false;
    }

    public tickSleepTimers(deltaTime: number): void
    {
        for (const frame of this._frames) {
            if (frame.sleepTimer > 0) {
                frame.sleepTimer -= deltaTime;
                if (frame.sleepTimer < 0) frame.sleepTimer = 0;
            }
        }

        for (let i = 0; i < this._fastSleepTimers.length; i++) {
            if (this._fastSleepTimers[i] > 0) {
                this._fastSleepTimers[i] -= deltaTime;
                if (this._fastSleepTimers[i] < 0) this._fastSleepTimers[i] = 0;
            }
        }

        if (this.sleepTime > 0) {
            this.sleepTime -= deltaTime;
            if (this.sleepTime < 0) this.sleepTime = 0;
        }
    }

    public setTopSleepTimer(duration: number): void
    {
        if (this._frameKinds.length === 0) {
            this.sleepTime = duration;
            return;
        }

        const topKind = this._frameKinds[this._frameKinds.length - 1];
        if (topKind === FRAME_KIND_SLOW) {
            this._topSlowFrame!.sleepTimer = duration;
            return;
        }

        this._fastSleepTimers[this._fastSleepTimers.length - 1] = duration;
    }

    public get isSleeping(): boolean
    {
        if (this._frameKinds.length > 0) {
            const topKind = this._frameKinds[this._frameKinds.length - 1];
            if (topKind === FRAME_KIND_SLOW) {
                return (this._topSlowFrame?.sleepTimer ?? 0) > 0;
            }

            return this._fastSleepTimers[this._fastSleepTimers.length - 1] > 0;
        }

        return this.sleepTime > 0;
    }

    public findProgramByHash(hash: string): Program | null
    {
        return this._programs[hash] || null;
    }

    private get topModuleName(): string | undefined
    {
        if (this._frameKinds.length === 0) {
            return undefined;
        }

        const topKind = this._frameKinds[this._frameKinds.length - 1];
        if (topKind === FRAME_KIND_SLOW) {
            return this._topSlowFrame?.moduleName;
        }

        return this._fastModuleNames[this._fastModuleNames.length - 1];
    }

    private refreshExecutionContext(): void
    {
        if (this._frameKinds.length === 0) {
            this._currentProgram = this._program;
            this._currentScope = this._scopes[this._program.hash] ?? this._mainScope;
            this.currentLocalSlots = [];
            return;
        }

        const topKind = this._frameKinds[this._frameKinds.length - 1];
        if (topKind === FRAME_KIND_SLOW) {
            this._currentProgram = this._topSlowFrame!.program;
            this._currentScope = this._scopes[this._currentProgram.hash] ?? this._mainScope;
            this.currentLocalSlots = this._topSlowFrame!.localSlots;
            return;
        }

        this._currentProgram = this._fastPrograms[this._fastPrograms.length - 1];
        this._currentScope = this._scopes[this._currentProgram.hash] ?? this._mainScope;
        this.currentLocalSlots = this._fastLocalSlots[this._fastLocalSlots.length - 1];
    }

    public isTruthy(value: any): boolean
    {
        if (value === null || value === undefined || value === false) {
            return false;
        }

        if (Array.isArray(value)) {
            return value.length > 0;
        }

        if (typeof value === 'number') {
            return value !== 0;
        }

        if (typeof value === 'string') {
            return value.length > 0;
        }

        return true;
    }
}

type PushFrameOptions = {
    program?: Program,
    isInterrupt?: boolean;
    isModule?: boolean;
    name?: string;
    moduleName?: string;
}
